import { Box, Button, Container, Toolbar, Typography } from "@mui/material";
import { lazy, Suspense, useEffect, useState } from "react";
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import LoginForm from "./Authentication/Login";
import Footer from "./components/Footer";
import Header from "./components/Header";
import Sidebar from "./components/Sidebar";
import useIdleLogout from "./components/useIdleLogout";
import { startSessionPing } from "./auth/sessionPing";
import { PermissionProvider } from "./PermissionContext";
import ProtectedRoute from "./ProtectedRoute";
import LaneProtectedRoute from "./LaneProtectedRoute";
import AuthenticatedRoute from "./AuthenticatedRoute";
import DepartmentQueryGuard from "./DepartmentQueryGuard";
import NetworkStatus from "./components/NetworkStatus";
import PasswordExpiryNotice from "./components/PasswordExpiryNotice";
import "../src/api/fetchWrapper";
import {
  clearCompanyCode,
  getAccessToken,
  getCompanyCode,
  hasSession,
  isAssignedCompany,
  needsPasswordChange,
  redirectToLogin,
  restoreSession,
  SESSION_OK,
  SESSION_UNAVAILABLE,
} from "./auth/authSession";
import { SETTINGS_ACCESS_PERMISSIONS } from "./utils/permissions";

const CompanyPage = lazy(() => import("./components/CompanyPage"));
const CompanyTable = lazy(() => import("./Company/CompanyTable"));
const Home = lazy(() => import("./components/Home"));
const Reports = lazy(() => import("./components/Reports"));
const Settings = lazy(() => import("./components/Settings"));
const FinanceTable = lazy(() => import("./Finance/FinanceTable"));
const GoodsReceivedNoteTable = lazy(() => import("./GoodsReceivedNote/GoodsReceivedNoteTable"));
const ImportExport = lazy(() => import("./ImportExport/ImportExport"));
const QuickBooksExport = lazy(() => import("./ImportExport/QuickBooksExport"));
const LocationUpload = lazy(() => import("./ImportExport/LocationUpload"));
const MaterialRequisitionsTable = lazy(() => import("./Materialrequisitions/MaterialRequisitionsTable"));
const MaterialsIssueNoteTable = lazy(() => import("./MaterialsIssueNote/MaterialsIssueNoteTable"));
const FuelIssueNoteTable = lazy(() => import("./MaterialsIssueNote/FuelIssueNoteTable"));
const ProductTable = lazy(() => import("./Products/ProductTable"));
const WarehouseMapPage = lazy(() => import("./Warehouse/WarehouseMapPage"));
const PurchaseOrderTable = lazy(() => import("./PurchaseOrder/PurchaseOrderTable"));
const SupplierTable = lazy(() => import("./Suppliers/SupplierTable"));
const UserPermissions = lazy(() => import("./Users/UserPermissions"));
const Users = lazy(() => import("./Users/Users"));
const UserTable = lazy(() => import("./Users/UserTable"));
const RoleTable = lazy(() => import("./Users/RoleTable"));
const AuditLogTable = lazy(() => import("./Users/AuditLogTable"));
const StockSummary = lazy(() => import("./Reports/StockSummary"));
const MinSummary = lazy(() => import("./Reports/MinSummary"));
const GrnSummary = lazy(() => import("./Reports/GrnSummary"));
const GrnItemWiseSummary = lazy(() => import("./Reports/GrnItemWiseSummary"));
const MrnSummary = lazy(() => import("./Reports/MrnSummary"));
const PoSummary = lazy(() => import("./Reports/PoSummary"));
const InventoryValuationReport = lazy(() => import("./Reports/InventoryValuationReport"));
const BillSummary = lazy(() => import("./Reports/BillSummary"));
const InvoiceReport = lazy(() => import("./Reports/InvoiceReport"));
const InventoryAgingReport = lazy(() => import("./Reports/InventoryAgingReport"));
const StockCard = lazy(() => import("./Reports/StockCard"));
const ProductBarcodeReport = lazy(() => import("./Reports/ProductBarcodeReport"));
const OpenPoReport = lazy(() => import("./Reports/OpenPoReport"));
const ApAgingReport = lazy(() => import("./Reports/ApAgingReport"));
const PaymentForecastReport = lazy(() => import("./Reports/PaymentForecastReport"));
const ReorderReport = lazy(() => import("./Reports/ReorderReport"));
const DepartmentConsumptionReport = lazy(() => import("./Reports/DepartmentConsumptionReport"));
const SpendSummaryReport = lazy(() => import("./Reports/SpendSummaryReport"));
const SupplierPerformanceReport = lazy(() => import("./Reports/SupplierPerformanceReport"));
const MrnAgingReport = lazy(() => import("./Reports/MrnAgingReport"));
const QuotationSavingsReport = lazy(() => import("./Reports/QuotationSavingsReport"));
const ForeignShipmentReport = lazy(() => import("./Reports/ForeignShipmentReport"));
const RepairVehicleCostReport = lazy(() => import("./Reports/RepairVehicleCostReport"));
const GatePassOutstandingReport = lazy(() => import("./Reports/GatePassOutstandingReport"));
const NonInventoryReceiptIssueReport = lazy(() => import("./Reports/NonInventoryReceiptIssueReport"));
const NonInventoryConsumptionReport = lazy(() => import("./Reports/NonInventoryConsumptionReport"));
const ForeignMRN = lazy(() => import("./ForeignExport/ForeignMRN"));
const Repairs = lazy(() => import("./components/Repairs"));
const ItemsTable = lazy(() => import("./Repairs/ItemsTable"));
const CategoryTable = lazy(() => import("./Products/Categories/CategoryTable"));
const DepartmentTable = lazy(() => import("./Products/Departments/DepartmentTable"));
const ClassTable = lazy(() => import("./MaterialsIssueNote/Classes/ClassTable"));
const MRNReport = lazy(() => import("./Reports/DislioReports/MRNReport"));
const POReport = lazy(() => import("./Reports/DislioReports/POReport"));
const POItemReport = lazy(() => import("./Reports/DislioReports/POItemReport"));
const GRNReport = lazy(() => import("./Reports/DislioReports/GRNReport"));
const GRNItemReport = lazy(() => import("./Reports/DislioReports/GRNItemReport"));
const MINReport = lazy(() => import("./Reports/DislioReports/MINReport"));
const MINItemReport = lazy(() => import("./Reports/DislioReports/MINItemReport"));
const BackDateInventoryValuation = lazy(() => import("./Reports/BackDateInventoryValuation"));
const VehiclesTable = lazy(() => import("./Repairs/Vehicles/VehiclesTable"));
const Table1 = lazy(() => import("./Repairs/Security/Table"));
const InventoryTable = lazy(() => import("./Repairs/Inventory/InventoryTable"));
const ReIssueTable = lazy(() => import("./Repairs/ReIssueItems/ReIssueTable"));
const Unauthorized = lazy(() => import("./Unauthorized"));
const FINSummary = lazy(() => import("./Reports/FINSummary"));
const FuelValuationReport = lazy(() => import("./Reports/FuelValuationReport"));
const BackDateFuelValuation = lazy(() => import("./Reports/BackDateFuelValuationReport"));
const ServiceMRNTable = lazy(() => import("./ServiceInventory/ServiceMRN/ServiceMRNTable"));
const ServicePOTable = lazy(() => import("./ServiceInventory/ServicePO/ServicePOTable"));
const DispatchApproval = lazy(() => import("./ServiceInventory/DispatchApproval/DispatchApprovalTable"));
const ServiceMINTable = lazy(() => import("./ServiceInventory/ServiceMIN/ServiceMINTable"));
const ServiceReturnTable = lazy(() => import("./ServiceInventory/ServiceReturn.jsx/ServiceReturnTable"));
const ServiceGRNTable = lazy(() => import("./ServiceInventory/ServiceGRN/ServiceGRNTable"));
const ServiceProductTable = lazy(() => import("./ServiceInventory/ServiceProducts.jsx/ServiceProductTable"));
const SecurityRepairs = lazy(() => import("./components/SecurityRepairs"));
const RepairItemsForSecurity = lazy(() => import("./ServiceInventory/RepairItemsForSecurity"));
const NewItemsForSecurity = lazy(() => import("./ServiceInventory/NewItemsForSecurity"));
const GatePass = lazy(() => import("./components/GatePass"));
const DispatchItemsTable = lazy(() => import("./GatePass/DispatchItems/DispatchItemsTable"));
const ReturnItemsTable = lazy(() => import("./GatePass/ReturnItems/ReturnItemsTable"));
const GatePassForSecurity = lazy(() => import("./ServiceInventory/GatePassForSecurity"));
const ChangePassword = lazy(() => import("./Authentication/ChangePassword"));

const ReportGuard = ({ children }) => (
  <ProtectedRoute requiredPermission="view_report">
    <DepartmentQueryGuard>{children}</DepartmentQueryGuard>
  </ProtectedRoute>
);

const NonInventoryReport = ({ children }) => (
  <ReportGuard>
    <LaneProtectedRoute>{children}</LaneProtectedRoute>
  </ReportGuard>
);

const SecurityGuard = ({ children }) => (
  <ProtectedRoute anyOf={["approve_security_out", "approve_security_in"]}>
    {children}
  </ProtectedRoute>
);

const GatePassGuard = ({ children }) => (
  <ProtectedRoute requiredPermission="view_maintenance_dispatch">
    {children}
  </ProtectedRoute>
);

const App = () => {
  return (
    <PermissionProvider>
      <Router>
        <MainContent />
      </Router>
    </PermissionProvider>
  );
};

const MainContent = () => {
  useIdleLogout(21600000); // 6 hours in milliseconds
  useEffect(() => startSessionPing(), []);
  const location = useLocation();
  const navigate = useNavigate();
  const isLoginPage = location.pathname === "/login";
  const isCompanyPage = location.pathname === "/company";
  const isChangePasswordPage = location.pathname === "/change-password";
  const [sessionReady, setSessionReady] = useState(isLoginPage);
  const [sessionUnavailable, setSessionUnavailable] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const hasAssignedCompany = isAssignedCompany(getCompanyCode());
  const showShell =
    !isLoginPage &&
    !isCompanyPage &&
    !isChangePasswordPage &&
    hasAssignedCompany;

  useEffect(() => {
    const loader = document.getElementById("loader");
    if (loader) loader.style.display = "none";

    if (isLoginPage) {
      setSessionReady(true);
      return;
    }

    const syncSelectedCompany = () => {
      const company = getCompanyCode();
      if (company && !isAssignedCompany(company)) {
        clearCompanyCode();
      }
      const selected = getCompanyCode();
      document.title = selected || "Colombo Logistics";
      if (needsPasswordChange() && !isChangePasswordPage) {
        navigate("/change-password", { replace: true });
        return;
      }
      if (
        !isCompanyPage &&
        !isChangePasswordPage &&
        !isAssignedCompany(selected)
      ) {
        navigate("/company", { replace: true });
      }
    };

    const bootstrap = async () => {
      setSessionUnavailable(false);
      if (hasSession() && getAccessToken()) {
        syncSelectedCompany();
        setSessionReady(true);
        return;
      }

      const restored = await restoreSession();
      if (restored === SESSION_OK) {
        syncSelectedCompany();
        setSessionReady(true);
        return;
      }
      if (restored === SESSION_UNAVAILABLE && hasSession()) {
        syncSelectedCompany();
        setSessionReady(true);
        return;
      }
      if (restored === SESSION_UNAVAILABLE) {
        setSessionUnavailable(true);
        setSessionReady(true);
        return;
      }
      redirectToLogin();
    };

    bootstrap();
  }, [isLoginPage, isCompanyPage, isChangePasswordPage, navigate]);

  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  if (!sessionReady && !isLoginPage) {
    return <div>Loading...</div>;
  }

  if (sessionUnavailable && !isLoginPage) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography sx={{ mb: 2 }}>
          Unable to reach the server. Retry to continue your login.
        </Typography>
        <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
          <Button variant="contained" onClick={() => window.location.reload()}>
            Retry
          </Button>
          <Button variant="outlined" onClick={redirectToLogin}>
            Go to login
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        minHeight: "100vh",
        bgcolor: "background.default",
      }}
    >
      <NetworkStatus />
      {showShell && (
        <Header onMenuClick={() => setMobileNavOpen(true)} />
      )}
      {showShell && <Toolbar />}
      {!isLoginPage && !isChangePasswordPage && hasSession() ? (
        <PasswordExpiryNotice />
      ) : null}
      <Box
        sx={{
          display: "flex",
          flexGrow: 1,
          mt: 0,
          ...((isLoginPage || isCompanyPage || isChangePasswordPage) && {
            flexGrow: 1,
            minHeight: 0,
            overflow: isChangePasswordPage || isCompanyPage ? "auto" : "hidden",
          }),
        }}
      >
        {showShell && (
          <Sidebar
            mobileOpen={mobileNavOpen}
            onMobileClose={() => setMobileNavOpen(false)}
          />
        )}
        <Container
          maxWidth={false}
          disableGutters
          sx={{
            flexGrow: 1,
            p: isLoginPage || isCompanyPage || isChangePasswordPage ? 0 : 2,
            width: "100%",
            maxWidth: "100%",
            boxSizing: "border-box",

            ...(isLoginPage
              ? {
                display: "flex",
                alignItems: "stretch",
                justifyContent: "center",
                overflow: "hidden",
              }
              : isCompanyPage
                ? {
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "center",
                  overflow: "auto",
                  bgcolor: "background.default",
                }
              : isChangePasswordPage
                ? {
                  overflowY: "auto",
                  overflowX: "hidden",
                }
                : {
                  overflowX: "auto",
                }),
          }}
        >
          <Suspense fallback={<div>Loading...</div>}>
          <Routes>
            <Route path="/" element={<Navigate to="/login" />} />
            <Route path="/login" element={<LoginForm />} />
            <Route
              path="/change-password"
              element={
                <AuthenticatedRoute allowPasswordChange>
                  <ChangePassword />
                </AuthenticatedRoute>
              }
            />
            <Route
              path="/unauthorized"
              element={
                <AuthenticatedRoute requireCompany>
                  <Unauthorized />
                </AuthenticatedRoute>
              }
            />
            <Route
              path="/home"
              element={
                <AuthenticatedRoute requireCompany>
                  <DepartmentQueryGuard>
                    <Home />
                  </DepartmentQueryGuard>
                </AuthenticatedRoute>
              }
            />
            <Route
              path="/company"
              element={
                <AuthenticatedRoute>
                  <CompanyPage />
                </AuthenticatedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute anyOf={SETTINGS_ACCESS_PERMISSIONS}>
                  <Settings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/user-settings"
              element={
                <ProtectedRoute requiredPermission="view_users">
                  <Users />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/data-import-export"
              element={
                <ProtectedRoute requiredPermission="export_quickbooks">
                  <ImportExport />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/data-import-export/quick-books-exports"
              element={
                <ProtectedRoute requiredPermission="export_quickbooks">
                  <QuickBooksExport />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/data-import-export/location-upload"
              element={
                <ProtectedRoute requiredPermission="export_quickbooks">
                  <LocationUpload />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/user-settings/user-table"
              element={
                <ProtectedRoute requiredPermission="view_users">
                  <UserTable />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/user-settings/user-permissions"
              element={
                <ProtectedRoute anyOf={["view_permission", "assign_permission"]}>
                  <UserPermissions />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/user-settings/role-table"
              element={
                <ProtectedRoute requiredPermission="assign_permission">
                  <RoleTable />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/audit-logs"
              element={
                <ProtectedRoute requiredPermission="view_audit">
                  <AuditLogTable />
                </ProtectedRoute>
              }
            />
            <Route
              path="/inventory/product-table"
              element={
                <LaneProtectedRoute>
                  <ProtectedRoute requiredPermission="view_product">
                    <ProductTable />
                  </ProtectedRoute>
                </LaneProtectedRoute>
              }
            />
            <Route
              path="/inventory/warehouse-map"
              element={
                <LaneProtectedRoute>
                  <ProtectedRoute requiredPermission="view_product">
                    <WarehouseMapPage />
                  </ProtectedRoute>
                </LaneProtectedRoute>
              }
            />
            <Route
              path="/non-inventory/product-table"
              element={
                <LaneProtectedRoute>
                  <ProtectedRoute requiredPermission="view_product">
                    <ProductTable />
                  </ProtectedRoute>
                </LaneProtectedRoute>
              }
            />
            <Route
              path="/inventory/material-requisitions-table"
              element={
                <LaneProtectedRoute>
                  <ProtectedRoute requiredPermission="view_mrn">
                    <MaterialRequisitionsTable />
                  </ProtectedRoute>
                </LaneProtectedRoute>
              }
            />
            <Route
              path="/non-inventory/material-requisitions-table"
              element={
                <LaneProtectedRoute>
                  <ProtectedRoute requiredPermission="view_mrn">
                    <MaterialRequisitionsTable />
                  </ProtectedRoute>
                </LaneProtectedRoute>
              }
            />
            <Route
              path="/inventory/supplier-table"
              element={
                <LaneProtectedRoute>
                  <ProtectedRoute requiredPermission="view_supplier">
                    <SupplierTable />
                  </ProtectedRoute>
                </LaneProtectedRoute>
              }
            />
            <Route
              path="/inventory/purchase-order-table"
              element={
                <LaneProtectedRoute>
                  <ProtectedRoute requiredPermission="view_po">
                    <PurchaseOrderTable />
                  </ProtectedRoute>
                </LaneProtectedRoute>
              }
            />
            <Route
              path="/non-inventory/purchase-order-table"
              element={
                <LaneProtectedRoute>
                  <ProtectedRoute requiredPermission="view_po">
                    <PurchaseOrderTable />
                  </ProtectedRoute>
                </LaneProtectedRoute>
              }
            />
            <Route
              path="/inventory/goods-received-Note-table"
              element={
                <LaneProtectedRoute>
                  <ProtectedRoute requiredPermission="view_grn">
                    <GoodsReceivedNoteTable />
                  </ProtectedRoute>
                </LaneProtectedRoute>
              }
            />
            <Route
              path="/non-inventory/goods-received-Note-table"
              element={
                <LaneProtectedRoute>
                  <ProtectedRoute requiredPermission="view_grn">
                    <GoodsReceivedNoteTable />
                  </ProtectedRoute>
                </LaneProtectedRoute>
              }
            />
            <Route
              path="/finance-table"
              element={
                <ProtectedRoute requiredPermission="view_finance">
                  <FinanceTable />
                </ProtectedRoute>
              }
            />
            <Route
              path="/inventory/materials-issue-Note-table"
              element={
                <LaneProtectedRoute>
                  <ProtectedRoute requiredPermission="view_min">
                    <MaterialsIssueNoteTable />
                  </ProtectedRoute>
                </LaneProtectedRoute>
              }
            />
            <Route
              path="/inventory/fuel-issue-Note-table"
              element={
                <LaneProtectedRoute>
                  <ProtectedRoute requiredPermission="view_min">
                    <FuelIssueNoteTable />
                  </ProtectedRoute>
                </LaneProtectedRoute>
              }
            />
            <Route path="/reports" element={<ReportGuard><Reports /></ReportGuard>} />
            <Route path="/reports/stock-summary" element={<ReportGuard><StockSummary /></ReportGuard>} />
            <Route path="/reports/min-summary" element={<ReportGuard><MinSummary /></ReportGuard>} />
            <Route path="/reports/fin-summary" element={<ReportGuard><FINSummary /></ReportGuard>} />
            <Route path="/reports/grn-summary" element={<ReportGuard><GrnSummary /></ReportGuard>} />
            <Route path="/reports/grn-item-wise-summary" element={<ReportGuard><GrnItemWiseSummary /></ReportGuard>} />
            <Route path="/reports/mrn-summary" element={<ReportGuard><MrnSummary /></ReportGuard>} />
            <Route path="/reports/po-summary" element={<ReportGuard><PoSummary /></ReportGuard>} />
            <Route path="/reports/inventory-valuation-report" element={<ReportGuard><InventoryValuationReport /></ReportGuard>} />
            <Route path="/reports/fuel-valuation-report" element={<ReportGuard><FuelValuationReport /></ReportGuard>} />
            <Route path="/reports/inventory-valuation-report/back-date-inventory-valuation" element={<ReportGuard><BackDateInventoryValuation /></ReportGuard>} />
            <Route path="/reports/fuel-valuation-report/back-date-fuel-valuation" element={<ReportGuard><BackDateFuelValuation /></ReportGuard>} />
            <Route path="/reports/bill-summary" element={<ReportGuard><BillSummary /></ReportGuard>} />
            <Route path="/reports/invoice-report" element={<ReportGuard><InvoiceReport /></ReportGuard>} />
            <Route path="/reports/inventory-aging-report" element={<ReportGuard><InventoryAgingReport /></ReportGuard>} />
            <Route path="/reports/stock-card" element={<ReportGuard><StockCard /></ReportGuard>} />
            <Route path="/reports/product-barcodes" element={<ReportGuard><ProductBarcodeReport /></ReportGuard>} />
            <Route path="/reports/open-po" element={<ReportGuard><OpenPoReport /></ReportGuard>} />
            <Route path="/reports/ap-aging" element={<ReportGuard><ApAgingReport /></ReportGuard>} />
            <Route path="/reports/payment-forecast" element={<ReportGuard><PaymentForecastReport /></ReportGuard>} />
            <Route path="/reports/reorder" element={<ReportGuard><ReorderReport /></ReportGuard>} />
            <Route path="/reports/department-consumption" element={<ReportGuard><DepartmentConsumptionReport /></ReportGuard>} />
            <Route path="/reports/spend-summary" element={<ReportGuard><SpendSummaryReport /></ReportGuard>} />
            <Route path="/reports/supplier-performance" element={<ReportGuard><SupplierPerformanceReport /></ReportGuard>} />
            <Route path="/reports/mrn-aging" element={<ReportGuard><MrnAgingReport /></ReportGuard>} />
            <Route path="/reports/quotation-savings" element={<ReportGuard><QuotationSavingsReport /></ReportGuard>} />
            <Route path="/reports/foreign-shipment" element={<ReportGuard><ForeignShipmentReport /></ReportGuard>} />
            <Route path="/reports/repair-vehicle-cost" element={<ReportGuard><RepairVehicleCostReport /></ReportGuard>} />
            <Route path="/reports/gate-pass-outstanding" element={<ReportGuard><GatePassOutstandingReport /></ReportGuard>} />
            <Route path="/non-inventory/reports/non-inventory-receipt-issue" element={<NonInventoryReport><NonInventoryReceiptIssueReport /></NonInventoryReport>} />
            <Route path="/non-inventory/reports/non-inventory-consumption" element={<NonInventoryReport><NonInventoryConsumptionReport /></NonInventoryReport>} />
            <Route path="/non-inventory/reports/mrn-summary" element={<NonInventoryReport><MrnSummary /></NonInventoryReport>} />
            <Route path="/non-inventory/reports/mrn-aging" element={<NonInventoryReport><MrnAgingReport /></NonInventoryReport>} />
            <Route path="/non-inventory/reports/po-summary" element={<NonInventoryReport><PoSummary /></NonInventoryReport>} />
            <Route path="/non-inventory/reports/open-po" element={<NonInventoryReport><OpenPoReport /></NonInventoryReport>} />
            <Route path="/non-inventory/reports/grn-summary" element={<NonInventoryReport><GrnSummary /></NonInventoryReport>} />
            <Route path="/non-inventory/reports/grn-item-wise-summary" element={<NonInventoryReport><GrnItemWiseSummary /></NonInventoryReport>} />
            <Route path="/non-inventory/reports/spend-summary" element={<NonInventoryReport><SpendSummaryReport /></NonInventoryReport>} />
            <Route path="/non-inventory/reports/supplier-performance" element={<NonInventoryReport><SupplierPerformanceReport /></NonInventoryReport>} />
            <Route path="/non-inventory/reports/quotation-savings" element={<NonInventoryReport><QuotationSavingsReport /></NonInventoryReport>} />
            <Route path="/non-inventory/reports/foreign-shipment" element={<NonInventoryReport><ForeignShipmentReport /></NonInventoryReport>} />
            <Route path="/non-inventory/reports/bill-summary" element={<NonInventoryReport><BillSummary /></NonInventoryReport>} />
            <Route path="/non-inventory/reports/invoice-report" element={<NonInventoryReport><InvoiceReport /></NonInventoryReport>} />
            <Route path="/non-inventory/reports/ap-aging" element={<NonInventoryReport><ApAgingReport /></NonInventoryReport>} />
            <Route path="/non-inventory/reports/payment-forecast" element={<NonInventoryReport><PaymentForecastReport /></NonInventoryReport>} />
            <Route path="/non-inventory/reports/department-consumption" element={<NonInventoryReport><DepartmentConsumptionReport /></NonInventoryReport>} />
            <Route
              path="/inventory/foreign-mrn"
              element={
                <LaneProtectedRoute>
                  <ProtectedRoute requiredPermission="view_foreign_shipment">
                    <ForeignMRN />
                  </ProtectedRoute>
                </LaneProtectedRoute>
              }
            />
            <Route
              path="/non-inventory/foreign-mrn"
              element={
                <LaneProtectedRoute>
                  <ProtectedRoute requiredPermission="view_foreign_shipment">
                    <ForeignMRN />
                  </ProtectedRoute>
                </LaneProtectedRoute>
              }
            />
            <Route
              path="/repairs"
              element={
                <ProtectedRoute requiredPermission="view_maintenance_dispatch">
                  <Repairs />
                </ProtectedRoute>
              }
            />
            <Route
              path="/gate-pass"
              element={
                <GatePassGuard>
                  <GatePass />
                </GatePassGuard>
              }
            />
            <Route path="/security-repairs" element={<SecurityGuard><SecurityRepairs /></SecurityGuard>} />
            <Route path="/security-repairs/repair-items-for-security" element={<SecurityGuard><RepairItemsForSecurity /></SecurityGuard>} />
            <Route path="/security-repairs/new-items-for-security" element={<SecurityGuard><NewItemsForSecurity /></SecurityGuard>} />
            <Route path="/security-repairs/gate-pass-for-security" element={<SecurityGuard><GatePassForSecurity /></SecurityGuard>} />
            <Route
              path="/repairs/items-table"
              element={
                <ProtectedRoute requiredPermission="view_maintenance_dispatch">
                  <ItemsTable />
                </ProtectedRoute>
              }
            />
            <Route
              path="/repairs/repairs-mrn-table"
              element={
                <ProtectedRoute anyOf={["create_maintenance_dispatch", "view_rmrn"]}>
                  <ServiceMRNTable />
                </ProtectedRoute>
              }
            />
            <Route
              path="/repairs/repairs-po-table"
              element={
                <ProtectedRoute anyOf={["view_rpo", "view_po"]}>
                  <ServicePOTable />
                </ProtectedRoute>
              }
            />
            <Route
              path="/repairs/dispatch-approvals-table"
              element={
                <ProtectedRoute requiredPermission="approve_security_out">
                  <DispatchApproval />
                </ProtectedRoute>
              }
            />
            <Route
              path="/repairs/repairs-return-table"
              element={
                <ProtectedRoute requiredPermission="approve_security_in">
                  <ServiceReturnTable />
                </ProtectedRoute>
              }
            />
            <Route
              path="/repairs/repairs-min-table"
              element={
                <ProtectedRoute anyOf={["create_service_issue", "view_rmin"]}>
                  <ServiceMINTable />
                </ProtectedRoute>
              }
            />
            <Route
              path="/repairs/repairs-grn-table"
              element={
                <ProtectedRoute anyOf={["create_maintenance_dispatch", "view_rgrn"]}>
                  <ServiceGRNTable />
                </ProtectedRoute>
              }
            />
            <Route
              path="/repairs/repairs-product-table"
              element={
                <ProtectedRoute requiredPermission="view_product">
                  <ServiceProductTable />
                </ProtectedRoute>
              }
            />
            <Route
              path="/repairs/vehicles-table"
              element={
                <ProtectedRoute requiredPermission="view_vehicle">
                  <VehiclesTable />
                </ProtectedRoute>
              }
            />
            <Route
              path="/repairs/security-table"
              element={
                <ProtectedRoute requiredPermission="view_maintenance_dispatch">
                  <Table1 />
                </ProtectedRoute>
              }
            />
            <Route
              path="/repairs/inventory-table"
              element={
                <ProtectedRoute requiredPermission="view_maintenance_dispatch">
                  <InventoryTable />
                </ProtectedRoute>
              }
            />
            <Route
              path="/repairs/re-issue-table"
              element={
                <ProtectedRoute requiredPermission="view_maintenance_dispatch">
                  <ReIssueTable />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/categories/category-table"
              element={
                <ProtectedRoute requiredPermission="view_product_category">
                  <CategoryTable />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/classes/class-table"
              element={
                <ProtectedRoute requiredPermission="view_min">
                  <ClassTable />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/departments/department-table"
              element={
                <ProtectedRoute requiredPermission="view_department">
                  <DepartmentTable />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/companies/company-table"
              element={
                <ProtectedRoute requiredPermission="view_company">
                  <CompanyTable />
                </ProtectedRoute>
              }
            />
            <Route path="/gate-pass/dispatch-items-table" element={<GatePassGuard><DispatchItemsTable /></GatePassGuard>} />
            <Route path="/gate-pass/return-items-table" element={<GatePassGuard><ReturnItemsTable /></GatePassGuard>} />
            <Route path="/reports/mrn-dislio-report" element={<ReportGuard><MRNReport /></ReportGuard>} />
            <Route path="/reports/po-dislio-report" element={<ReportGuard><POReport /></ReportGuard>} />
            <Route path="/reports/po-item-dislio-report" element={<ReportGuard><POItemReport /></ReportGuard>} />
            <Route path="/reports/grn-dislio-report" element={<ReportGuard><GRNReport /></ReportGuard>} />
            <Route path="/reports/grn-item-dislio-report" element={<ReportGuard><GRNItemReport /></ReportGuard>} />
            <Route path="/reports/min-dislio-report" element={<ReportGuard><MINReport /></ReportGuard>} />
            <Route path="/reports/min-item-dislio-report" element={<ReportGuard><MINItemReport /></ReportGuard>} />
          </Routes>
          </Suspense>
        </Container>
      </Box>
      {!isLoginPage && !isChangePasswordPage ? (
        <Box sx={{ pb: isCompanyPage ? 3 : 0 }}>
          <Footer />
        </Box>
      ) : null}
    </Box>
  );
};

export default App;
